import { css, html, LitElement, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type {
  AccountUpdateMessageReceiveRemindersEnum,
  AccountUpdateMessageSummariesEnum,
} from '@trinnvis/dabih-api-client';
import '../../library/editors/elements/d-edit-text.js';
import '../../library/editors/elements/d-select-radio.js';
import '../../library/editors/elements/d-checkbox';
import '../../library/elements/d-section.js';
import '../../library/presentational/d-sections.js';
import { headerViewStyles } from 'src/layout/parts/header-view-styles.js';
import { currentLocaleTag, t } from 'src/i18n';

import type { UiSettings } from 'src/store/selectors/uiSettings';
import type { FeatureStates } from 'src/store/selectors/featureStates';
import type { ProfileUser } from './profile-user';
import { runningUnderNativePlatform } from '../../store/running-under-native-platform';
// Device import moved to dynamic import in getPlatformInfoHtml() to avoid loading in browser
import { LiveUpdate } from '@capawesome/capacitor-live-update';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';

import './d-organization-internal-settings';
import './d-calendar-settings';
import { isEmailValid } from 'src/store/selectors';

/**
 *
 * STATUS OK
 */
@customElement('d-organization-profile')
export class DOrganizationProfile extends LitElement {
  private get alertOptions() {
    return [
      {
        value: 'ALL',
        text: t('layout.profile.alertOptionAll'),
      },
      { value: 'SCHEDULED', text: t('layout.profile.alertOptionScheduled') },
      { value: 'NONE', text: t('layout.profile.alertOptionNone') },
    ];
  }
  private get receiveRemindersOptions() {
    return [
      { value: 'NONE', text: t('layout.profile.reminderOptionNone') },
      { value: 'SMS', text: t('layout.profile.reminderOptionSms') },
    ];
  }
  static readonly styles = [
    headerViewStyles,
    css`
      :host {
        display: block;
      }
    `,
  ];
  @property({ type: Number })
  organizationId = 0;
  @property({ type: Object })
  user!: ProfileUser;
  @property({ type: String })
  newEmail = '';
  @property({ type: String })
  newPassword = '';
  @property({ type: String })
  newPasswordConfirm = '';
  @property({ type: String })
  lastPassword = '';
  @property({ type: Object })
  employee = {};
  @property({ type: String })
  alerts = 'NONE';
  @property({ type: String })
  receiveReminders = 'NONE';
  @property({ type: Array })
  features: string[] = [];
  @property({ type: String })
  ownerEmail = '';
  @property({ type: Object })
  organization: any = {};
  @property({ type: Boolean })
  isEmployee = false;
  @property({ type: Object })
  featureStates!: FeatureStates;
  @property({ type: Boolean })
  storybook = false;
  @property({ type: Object })
  uiSettings: UiSettings = {
    isSimplifiedUi: false,
  };
  @state()
  private platformInfoHtml: string = t('layout.profile.platformInfoLoading');
  @state()
  private bundleId: string | null = null;
  @state()
  private deviceId: string | null = null;
  @state()
  private lastSyncTime: string | null = null;
  @state()
  private showPassword = false;

  private get appVersion() {
    if (this.storybook) {
      return new Date('2000-01-01').toLocaleString();
    }
    return new Date(__APP_VERSION__).toLocaleString();
  }

  private get isNewEmailInvalid(): boolean {
    return !isEmailValid(this.newEmail);
  }

  private get isNewEmailOld(): boolean {
    return this.newEmail === this.user.username;
  }

  private toggleShowPassword() {
    this.showPassword = !this.showPassword;
  }

  private get updateCredentialsDisabled(): boolean {
    if (this.isNewEmailInvalid) {
      return true;
    }
    if (this.newPassword.length > 0 && this.newPassword.length < 4) {
      return true;
    }
    if (this.newPassword !== this.newPasswordConfirm) {
      return true;
    }
    return this.isNewEmailOld && this.newPassword === '';
  }

  renderWeeklyAlert() {
    if (this.featureStates.core) {
      return html`
        <div class="settings-section">
          <h2>${t('layout.profile.weeklyAlertHeading')}</h2>
          <p>${t('layout.profile.weeklyAlertText')}</p>
          <d-select-radio
            vertical
            outskirts
            .options=${this.alertOptions}
            .value=${this.alerts}
            @value-changed=${this.onAlertsChanged}
          >
          </d-select-radio>
        </div>
      `;
    }
    return nothing;
  }

  renderCalendarSettings() {
    // Always render calendar settings, let the component check for EXTERNAL_CALENDAR feature
    return html`
      <d-calendar-settings
        .organizationId=${this.organizationId}
        .user=${this.user}
        .features=${this.features}
      ></d-calendar-settings>
    `;
  }

  renderEmployeeOptions() {
    if (this.isEmployee) {
      return html`
        ${this.renderWeeklyAlert()}
        <div class="settings-section">
          <h2>${t('layout.profile.smsRemindersHeading')}</h2>
          <p>${t('layout.profile.smsRemindersText')}</p>
          <d-select-radio
            vertical
            outskirts
            .options=${this.receiveRemindersOptions}
            .value=${this.receiveReminders}
            @value-changed=${this.onReceiveRemindersChanged}
          >
          </d-select-radio>
        </div>
      `;
    }
    return nothing;
  }

  render() {
    return html`
      ${this.ownerEmail.endsWith('@trinnvis.no') || this.ownerEmail.endsWith('@mailinator.com')
        ? html` <d-organization-internal-settings .features=${this.features}></d-organization-internal-settings> `
        : nothing}
      ${this.renderEmployeeOptions()}
      <div class="settings-section">
        <h2>${t('layout.profile.usernameAndPasswordHeading')}</h2>
        <d-wrap>
          <d-edit-text
            outskirts
            style="flex-grow: 1"
            class="minWidth200"
            label="${t('layout.profile.username')}"
            sublabel="${t('layout.profile.usernameSublabel')}"
            .value=${this.newEmail}
            ?invalid=${!isEmailValid(this.newEmail)}
            @value-changed=${this.onNewEmailChanged}
          ></d-edit-text>
          <d-wrap>
            <d-edit-text
              outskirts
              style="flex-grow: 1"
              autocomplete="new-password"
              class="minWidth200"
              label="${t('layout.profile.newPassword')}"
              type="${this.showPassword ? 'text' : 'password'}"
              .value=${this.newPassword}
              .labelAction=${this.showPassword ? t('layout.profile.hide') : t('layout.profile.show')}
              label-action-plain
              ?invalid=${this.newPassword.length > 0 && this.newPassword.length < 4}
              @value-changed=${this.onNewPasswordChanged}
              @label-action=${() => this.toggleShowPassword()}
            ></d-edit-text>
            <d-edit-text
              outskirts
              style="flex-grow: 1"
              autocomplete="new-password"
              class="minWidth200"
              label="${t('layout.profile.repeatNewPassword')}"
              type="${this.showPassword ? 'text' : 'password'}"
              .value=${this.newPasswordConfirm}
              ?invalid=${this.newPassword !== this.newPasswordConfirm}
              @value-changed=${this.onNewPasswordConfirmChanged}
            ></d-edit-text>
          </d-wrap>
        </d-wrap>
        <div class="bottom-right-buttons">
          <div
            class="action update-credentials"
            @click=${this.onUpdateCredentials}
            ?disabled=${this.updateCredentialsDisabled}
          >
            ${t('layout.profile.update')}
          </div>
        </div>
      </div>

      <div class="settings-section">
        <h2>${t('layout.profile.bankIdHeading')}</h2>
        <d-wrap split baseline>
          <p>
            ${this._hasCriiptoAuthDescription(this.user)
              ? t('layout.profile.bankIdConnected', { description: this.user.criiptoAuthDescription })
              : t('layout.profile.bankIdNotConnected')}
          </p>
          <div>
            ${this._hasCriiptoAuthDescription(this.user)
              ? html` <div class="action" @click=${() => this._disconnectFromAuthProviderCriipto()}>
                  ${t('layout.profile.disconnect')}
                </div>`
              : html` <div class="action" @click=${() => this._connectToAuthProviderCriipto()}>
                  ${t('layout.profile.connect')}
                </div>`}
          </div>
        </d-wrap>
      </div>

      <div class="settings-section">
        <h2>${t('layout.profile.helseIdHeading')}</h2>
        <d-wrap split baseline>
          <p>
            ${this._hasAuthDescription(this.user)
              ? t('layout.profile.helseIdConnected', { description: this.user.authDescription })
              : t('layout.profile.helseIdNotConnected')}
          </p>
          <div>
            ${this._hasAuthDescription(this.user)
              ? html` <div class="action" @click=${() => this._disconnectFromAuthProvider()}>
                  ${t('layout.profile.disconnect')}
                </div>`
              : html` <div class="action" @click=${() => this._connectToAuthProvider()}>
                  ${t('layout.profile.connect')}
                </div>`}
          </div>
        </d-wrap>
      </div>

      <div class="settings-section">
        <h2>${t('layout.profile.versionHeading')}</h2>
        <d-wrap split baseline>
          <p>${this.appVersion}</p>
          <div>
            ${runningUnderNativePlatform()
              ? html`
                  ${unsafeHTML(this.platformInfoHtml)}
                  ${this.bundleId !== null
                    ? html`<p>
                        ${unsafeHTML(t('layout.profile.bundleIdLabel'))}
                        ${this.bundleId || t('layout.profile.bundleIdDefault')}
                      </p>`
                    : nothing}
                  ${this.deviceId !== null
                    ? html`<p>${unsafeHTML(t('layout.profile.deviceIdLabel'))} ${this.deviceId}</p>`
                    : nothing}
                  ${this.lastSyncTime !== null
                    ? html`<p>${unsafeHTML(t('layout.profile.lastSyncLabel'))} ${this.lastSyncTime}</p>`
                    : nothing}
                `
              : html` <div class="action" @click=${() => this.forceServiceWorkerUpdate()}>
                  ${t('layout.profile.updateVersion')}
                </div>`}
          </div>
        </d-wrap>
      </div>
      ${this.renderCalendarSettings()}
    `;
  }

  async getPlatformInfoHtml(): Promise<string> {
    // Only call Device.getInfo() when running under native platform
    if (!runningUnderNativePlatform()) {
      return t('layout.profile.platformInfoNativeOnly');
    }

    try {
      // Dynamic import to avoid loading @capacitor/device in browser environments
      const { Device } = await import('@capacitor/device');
      const info = await Device.getInfo();
      const browser = info.platform === 'web' ? this.detectBrowser() : 'N/A';
      const notAvailable = t('layout.profile.notAvailable');

      return t('layout.profile.platformInfo', {
        platform: info.platform,
        browser,
        operatingSystem: info.operatingSystem || notAvailable,
        osVersion: info.osVersion || notAvailable,
        manufacturer: info.manufacturer || notAvailable,
        model: info.model || notAvailable,
        webViewVersion: info.webViewVersion || notAvailable,
        isVirtual:
          typeof info.isVirtual === 'boolean'
            ? info.isVirtual
              ? t('layout.profile.yes')
              : t('layout.profile.no')
            : notAvailable,
        memUsed:
          typeof info.memUsed === 'number' ? info.memUsed.toFixed(2) + t('layout.profile.memUsedSuffix') : notAvailable,
      });
    } catch (error) {
      console.error('Failed to get device info:', error);
      return t('layout.profile.platformInfoError');
    }
  }

  async firstUpdated() {
    this.platformInfoHtml = await this.getPlatformInfoHtml();
    await this.loadLiveUpdateInfo();
  }

  async loadLiveUpdateInfo() {
    if (runningUnderNativePlatform()) {
      try {
        // Get the actual device ID
        const { deviceId } = await LiveUpdate.getDeviceId();
        this.deviceId = deviceId;

        // Get current bundle ID
        const { bundleId } = await LiveUpdate.getCurrentBundle();
        this.bundleId = bundleId;

        // Get last sync time from localStorage
        const lastSync = localStorage.getItem('liveUpdateLastSync');
        if (lastSync) {
          const date = new Date(lastSync);
          this.lastSyncTime = date.toLocaleString(currentLocaleTag());
        }
      } catch (error) {
        console.error('Error loading LiveUpdate info:', error);
      }
    }
  }

  private detectBrowser(): string {
    const ua = navigator.userAgent;

    if (/chrome|crios/i.test(ua) && !/edge|edg/i.test(ua)) {
      return 'Chrome';
    } else if (/firefox|fxios/i.test(ua)) {
      return 'Firefox';
    } else if (/safari/i.test(ua) && !/chrome|crios|android/i.test(ua)) {
      return 'Safari';
    } else if (/edg/i.test(ua)) {
      return 'Edge';
    } else if (/opera|opr\//i.test(ua)) {
      return 'Opera';
    } else {
      return 'Unknown';
    }
  }

  private async forceServiceWorkerUpdate() {
    if (!('serviceWorker' in navigator)) {
      window.location.reload();
      return;
    }
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration) {
      window.location.reload();
      return;
    }

    // Reload først når den nye SW-en faktisk tar over, ellers serverer den
    // gamle SW-en gammel index.html/assets fra precache på nytt. SW-en kaller
    // skipWaiting() i install, så controllerchange fyrer når ny SW aktiverer.
    navigator.serviceWorker.addEventListener('controllerchange', () => window.location.reload(), {
      once: true,
    });

    await registration.update();

    // Ingen ny versjon ventende/installerende → tvungen reload for fersk navigasjon.
    if (!registration.installing && !registration.waiting) {
      window.location.reload();
    }
  }

  private onUpdateAlerts() {
    this.dispatchEvent(
      new CustomEvent<{
        email: string;
        password?: string;
        alerts: string;
        receiveReminders: string;
      }>('update-user', {
        composed: true,
        bubbles: true,
        detail: {
          email: this.user.username,
          password: undefined,
          alerts: this.alerts as AccountUpdateMessageSummariesEnum,
          receiveReminders: this.receiveReminders as AccountUpdateMessageReceiveRemindersEnum,
        },
      }),
    );
  }

  private onUpdateCredentials() {
    if (!this.updateCredentialsDisabled) {
      this.dispatchEvent(
        new CustomEvent<{
          email: string;
          password?: string;
          alerts: string;
          receiveReminders: string;
        }>('update-user', {
          composed: true,
          bubbles: true,
          detail: {
            email: this.newEmail,
            password: this.newPassword === '' ? undefined : this.newPassword,
            alerts: this.alerts as AccountUpdateMessageSummariesEnum,
            receiveReminders: this.receiveReminders as AccountUpdateMessageReceiveRemindersEnum,
          },
        }),
      );
      this.newPassword = '';
      this.newPasswordConfirm = '';
    }
  }

  private onAlertsChanged(e: CustomEvent<{ value: string }>) {
    e.stopPropagation();
    this.alerts = e.detail.value;
    this.onUpdateAlerts();
  }

  private onReceiveRemindersChanged(e: CustomEvent<{ value: string }>) {
    e.stopPropagation();
    this.receiveReminders = e.detail.value;
    this.onUpdateAlerts();
  }

  private onNewEmailChanged(e: CustomEvent<{ value: string }>) {
    e.stopPropagation();
    this.newEmail = e.detail.value;
  }

  private onNewPasswordChanged(e: CustomEvent<{ value: string }>) {
    e.stopPropagation();
    this.newPassword = e.detail.value;
  }

  private onNewPasswordConfirmChanged(e: CustomEvent<{ value: string }>) {
    e.stopPropagation();
    this.newPasswordConfirm = e.detail.value;
  }

  private _connectToAuthProvider() {
    this.dispatchEvent(
      new CustomEvent<{ provider: 'oidc' | 'criipto' }>('link-auth', {
        composed: true,
        bubbles: true,
        detail: { provider: 'oidc' },
      }),
    );
  }

  private _disconnectFromAuthProvider() {
    this.dispatchEvent(
      new CustomEvent<{ provider: 'oidc' | 'criipto' }>('unlink-auth', {
        composed: true,
        bubbles: true,
        detail: { provider: 'oidc' },
      }),
    );
  }

  private _connectToAuthProviderCriipto() {
    this.dispatchEvent(
      new CustomEvent<{ provider: 'oidc' | 'criipto' }>('link-auth', {
        composed: true,
        bubbles: true,
        detail: { provider: 'criipto' },
      }),
    );
  }

  private _disconnectFromAuthProviderCriipto() {
    this.dispatchEvent(
      new CustomEvent<{ provider: 'oidc' | 'criipto' }>('unlink-auth', {
        composed: true,
        bubbles: true,
        detail: { provider: 'criipto' },
      }),
    );
  }

  private _hasCriiptoAuthDescription(user: { criiptoAuthDescription: string }) {
    return user.criiptoAuthDescription !== '';
  }

  private _hasAuthDescription(user: { authDescription: string }) {
    return user.authDescription !== '';
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-organization-profile': DOrganizationProfile;
  }
}
